import Papa from 'papaparse';

// Where to load place data from. Prefer the published Google Sheet CSV URL set in
// VITE_SHEET_CSV_URL; otherwise fall back to the bundled sample so the app still runs.
const SHEET_URL = import.meta.env.VITE_SHEET_CSV_URL?.trim();
const FALLBACK_URL = `${import.meta.env.BASE_URL}sample-places.csv`;

/**
 * @typedef {Object} Place
 * @property {string} name
 * @property {string} type        - primary lowercased category key, used for marker colour (e.g. "food")
 * @property {string[]} types     - all lowercased category keys for this place
 * @property {string[]} typeLabels - all original-cased type labels for display
 * @property {string} suburb
 * @property {number} lat
 * @property {number} lng
 * @property {string} [description]
 * @property {string} [address]
 * @property {string} [priceRange]
 * @property {string} [rating]
 * @property {string} [imageUrl]
 * @property {string} [website]
 */

/** Trim a CSV cell to a clean string ("" if missing). */
function clean(value) {
  return (value ?? '').toString().trim();
}

/**
 * Normalize one parsed CSV row into a Place, or null if it lacks valid coordinates.
 * @param {Record<string, string>} row
 * @param {number} index - row index (for warning messages)
 * @returns {Place | null}
 */
function toPlace(row, index) {
  const name = clean(row.name);
  const lat = Number.parseFloat(clean(row.latitude));
  const lng = Number.parseFloat(clean(row.longitude));

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    console.warn(
      `[data] Skipping row ${index + 2} ("${name || 'unnamed'}") — missing or invalid latitude/longitude.`,
    );
    return null;
  }

  // The type cell is a tag list: one or more labels separated by , ; / or |.
  const typeLabels = clean(row.type)
    .split(/[,;/|]/)
    .map((t) => t.trim())
    .filter(Boolean);
  if (typeLabels.length === 0) typeLabels.push('Other');
  const types = typeLabels.map((t) => t.toLowerCase());

  return {
    name: name || 'Unnamed place',
    type: types[0],
    types,
    typeLabels,
    suburb: clean(row.suburb) || 'Unknown',
    lat,
    lng,
    description: clean(row.description),
    address: clean(row.address),
    priceRange: clean(row.price_range),
    rating: clean(row.rating),
    imageUrl: clean(row.image_url),
    website: clean(row.website),
  };
}

/**
 * Fetch and parse the place data.
 * @returns {Promise<Place[]>}
 */
export async function loadPlaces() {
  const url = SHEET_URL || FALLBACK_URL;
  const usingFallback = !SHEET_URL;

  if (usingFallback) {
    console.info('[data] VITE_SHEET_CSV_URL not set — using bundled sample-places.csv.');
  }

  const response = await fetch(url, { cache: 'no-store' });
  if (!response.ok) {
    throw new Error(`Failed to load place data (HTTP ${response.status}) from ${url}`);
  }

  const csv = await response.text();
  const { data, errors } = Papa.parse(csv, {
    header: true,
    skipEmptyLines: true,
    transformHeader: (h) => h.trim().toLowerCase(),
  });

  if (errors.length) {
    console.warn('[data] CSV parse warnings:', errors);
  }

  const places = data.map(toPlace).filter((p) => p !== null);

  if (places.length === 0) {
    throw new Error(
      'No valid places found. Check that the sheet has name, type, suburb, latitude and longitude columns with data.',
    );
  }

  return places;
}

/**
 * Distinct, sorted display labels for a given facet key ("typeLabels" or "suburb").
 * Array-valued facets (e.g. typeLabels) are flattened so each tag counts once.
 */
export function distinctValues(places, key) {
  const all = places.flatMap((p) => {
    const value = p[key];
    return Array.isArray(value) ? value : [value];
  });
  return [...new Set(all.filter(Boolean))].sort((a, b) => a.localeCompare(b));
}
